import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plane, FileText, Clock, CheckCircle, AlertCircle, Plus, ArrowLeft, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  submitted: { bg: "bg-blue-50", text: "text-blue-600", icon: Clock },
  processing: { bg: "bg-blue-50", text: "text-blue-600", icon: Clock },
  approved: { bg: "bg-emerald-50", text: "text-emerald-600", icon: CheckCircle },
  rejected: { bg: "bg-red-50", text: "text-red-600", icon: AlertCircle },
};

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchApps();
  }, [user]);

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/applications`,  { headers: { Authorization: `Bearer ${token}` } });
      setApplications(data);
    } catch {} finally { setLoading(false); }
  };

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center bg-slate-50" ><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50" >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 transition-colors mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </button>
            <h1 className="heading-font text-3xl font-bold text-slate-900">Welcome, {user.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Track and manage your visa applications</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate("/profile")} data-testid="profile-btn"
              className="flex items-center gap-2 text-sm text-slate-600 px-4 py-3 rounded-full border border-slate-200 hover:bg-slate-50 transition-all">
              <User className="w-4 h-4" /> My Profile
            </button>
            <button onClick={() => navigate("/apply")} data-testid="new-application-btn"
              className="flex items-center gap-2 text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-full hover:bg-blue-700 transition-all">
              <Plus className="w-4 h-4" /> New Application
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total", value: applications.length, color: "text-slate-900" },
            { label: "Submitted", value: applications.filter(a => a.status === "submitted").length, color: "text-blue-600" },
            { label: "Approved", value: applications.filter(a => a.status === "approved").length, color: "text-emerald-600" },
            { label: "Processing", value: applications.filter(a => a.status === "processing").length, color: "text-blue-600" },
          ].map((s) => (
            <div key={s.label} className="card-elevated rounded-xl p-5">
              <p className="text-xs text-slate-400 mb-1">{s.label}</p>
              <p className={`heading-font text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Applications list */}
        {loading ? (
          <div className="text-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : applications.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-elevated rounded-2xl p-12 text-center">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="heading-font text-xl font-semibold text-slate-900 mb-2">No Applications Yet</h3>
            <p className="text-sm text-slate-500 mb-6">Start your visa journey by submitting your first application.</p>
            <button onClick={() => navigate("/apply")} data-testid="start-first-app"
              className="text-sm text-white font-semibold bg-blue-600 px-6 py-3 rounded-full hover:bg-blue-700 transition-all">
              Start Application
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {applications.map((app, i) => {
              const st = statusColors[app.status] || statusColors.submitted;
              const StIcon = st.icon;
              return (
                <motion.div key={app.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="card-elevated rounded-xl p-5 cursor-pointer" data-testid={`application-${app.id}`} onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-2xl">{app.country_code === "US" ? "\u{1F1FA}\u{1F1F8}" : app.country_code === "GB" ? "\u{1F1EC}\u{1F1E7}" : app.country_code === "CA" ? "\u{1F1E8}\u{1F1E6}" : app.country_code === "AE" ? "\u{1F1E6}\u{1F1EA}" : "\u{1F30D}"}</div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">{app.destination_country_name || app.country_name} - {app.visa_type}</h4>
                        <p className="text-xs text-slate-400">{app.processing_type || "standard"} | Applied {new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${st.bg} ${st.text}`}>
                      <StIcon className="w-3.5 h-3.5" /> {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                    </span>
                  </div>
                  {selectedApp?.id === app.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-5 pt-5 border-t border-slate-100 space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-slate-400">Full Name</span><p className="text-slate-900">{app.full_name}</p></div>
                        <div><span className="text-slate-400">Passport</span><p className="text-slate-900">{app.passport_number}</p></div>
                        <div><span className="text-slate-400">Travel Date</span><p className="text-slate-900">{app.travel_date}</p></div>
                        <div><span className="text-slate-400">Email Sent</span><p className="text-slate-900">{app.email_sent ? "Yes" : "No"}</p></div>
                      </div>
                      {app.status_history && (
                        <div className="mt-4">
                          <h5 className="text-xs text-slate-400 mb-2">Timeline</h5>
                          {app.status_history.map((h, j) => (
                            <div key={j} className="flex items-start gap-3 mb-2">
                              <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                              <div>
                                <p className="text-xs text-slate-900">{h.status.charAt(0).toUpperCase() + h.status.slice(1)}</p>
                                <p className="text-xs text-slate-400">{h.note} - {new Date(h.date).toLocaleDateString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 mt-4">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/tracking/${app.id}`); }}
                          className="text-xs text-blue-600 px-3 py-1.5 rounded-full border border-blue-200 hover:bg-blue-50">Track</button>
                        {app.status === "draft" && <>
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/apply/${app.id}/documents`); }}
                            className="text-xs text-slate-500 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50">Upload Docs</button>
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/apply/${app.id}/payment`); }}
                            className="text-xs text-white bg-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-700">Pay & Submit</button>
                        </>}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Mobile FAB */}
        <button onClick={() => navigate("/apply")} className="md:hidden fixed bottom-6 right-6 w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg hover:bg-blue-700 transition-all z-40">
          <Plus className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
